import { CLIENT_EVENT, EventLogger } from "@app/config/loggers/eventLogger";
import { apiClient } from "@base/api/client";
import { Button, Dialog } from "@buildblockinc/buildblock-design-system";
import dayjs from "dayjs";
import { useState } from "react";
import { toast } from "sonner";

interface ExportWorkHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  startDate: string;
  endDate: string;
}

export function ExportWorkHistoryDialog({
  open,
  onClose,
  projectId,
  startDate,
  endDate,
}: ExportWorkHistoryDialogProps) {
  const [isLoading, setIsLoading] = useState(false);

  const handleExport = async () => {
    // 내보내기는 존치 판단이 필요한 기능이라 클릭/성공/실패를 모두 남긴다.
    const rangeDays = dayjs(endDate).diff(dayjs(startDate), "day") + 1;
    const baseProperties = { project_id: projectId, range_days: rangeDays };

    EventLogger.click(CLIENT_EVENT.WORK_HISTORY_EXPORT_CLICK, baseProperties);

    const startedAt = performance.now();
    setIsLoading(true);
    try {
      const response = await apiClient.get<{ url: string }>("/project-management/api/analytics/summary/export", {
        params: { project_id: projectId, start_date: startDate, end_date: endDate },
      });
      const a = document.createElement("a");
      a.href = response.data.url;
      a.download = "";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      EventLogger.result(CLIENT_EVENT.WORK_HISTORY_EXPORT_SUCCESS, {
        ...baseProperties,
        duration_ms: Math.round(performance.now() - startedAt),
      });

      onClose();
    } catch {
      EventLogger.result(CLIENT_EVENT.WORK_HISTORY_EXPORT_FAIL, {
        ...baseProperties,
        duration_ms: Math.round(performance.now() - startedAt),
      });
      toast.error("Failed to export work history. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <Dialog.Content className="px-4 pt-8 pb-4">
        <Dialog.Title>Export work history</Dialog.Title>
        <Dialog.Description className="mt-6">
          This will export work history from [{dayjs(`${startDate}T00:00:00`).format("MMM DD, YYYY")}] to [
          {dayjs(`${endDate}T00:00:00`).format("MMM DD, YYYY")}] as an Excel file.
        </Dialog.Description>
        <div className="flex gap-2 mt-6">
          <Button type="button" variant="ghost" color="secondary" className="flex-1" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="fill"
            color="info"
            className="flex-1"
            onClick={handleExport}
            isLoading={isLoading}
          >
            Export
          </Button>
        </div>
      </Dialog.Content>
    </Dialog>
  );
}
